/**
 * GlobalLayout - Hive-OS Style Global Layout
 *
 * Features:
 * - Ambient background blurs (blue + amber)
 * - Responsive layout with device awareness
 * - Auto-collapse sidebar on tablet or when chat open (< 1440px)
 * - Chat panel: full-screen overlay on mobile, slide-in on desktop
 * - Mobile bottom navigation
 * - Smooth transitions (700ms for layout, 300ms for sidebar)
 */
import React, { Suspense, useState, useEffect, useCallback, Component, type ErrorInfo, type ReactNode } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { CopilotKit } from '@copilotkit/react-core';
import '@copilotkit/react-ui/styles.css';
import { Toaster } from 'react-hot-toast';
import { Sidebar } from '../sidebar/Sidebar';
import { TopBar } from '../header/TopBar';
import { MobileBottomNav } from '../navigation/MobileBottomNav';
import RightSidebarCopilot from '../sidebar/RightSidebarCopilot';
import { useDeviceAwareness, useSidebarAutoCollapse } from '@/hooks/useDeviceAwareness';
import { LayoutHintsProvider, useLayoutHints } from '@/hooks/useLayoutHints';
import { useAuth } from '@/contexts/AuthContext';
import { agents } from '@/lib/agents.config';
import { apiClient } from '@/lib/api-client';

/** Error boundary so CopilotKit failures don't crash the whole app */
class CopilotErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.warn('CopilotKit error (non-fatal):', error.message);
  }
  render() {
    if (this.state.hasError) return <>{this.props.children}</>;
    return this.props.children;
  }
}

const runtimeUrl = import.meta.env.VITE_COPILOT_RUNTIME_URL || '/api/user/copilotkit'

/** Outer wrapper — provides LayoutHintsContext so inner layout can read it */
const GlobalLayout: React.FC = () => (
  <LayoutHintsProvider>
    <GlobalLayoutInner />
  </LayoutHintsProvider>
);

const GlobalLayoutInner: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile, isLargeDesktop } = useDeviceAwareness();
  const { isDetailPanelOpen } = useLayoutHints();
  const { currentOrganization, onboardingProgress } = useAuth();

  // Chat panel state — read guide flag synchronously during init so the
  // sidebar opens on the very first render (no useEffect delay, StrictMode-safe).
  // Primary: URL ?guide=1 param (bulletproof, travels with navigation).
  // Secondary: onboarding status is "in_progress" (state-driven, persists across sessions).
  // Fallback: sessionStorage flag (legacy, can be lost to HMR/timing).
  const isOnboardingActive = onboardingProgress?.status === 'in_progress';
  const [isChatOpen, setIsChatOpen] = useState(() => {
    const fromUrl = new URLSearchParams(window.location.search).get('guide') === '1';
    const fromStorage = sessionStorage.getItem('sb_guide_onboarding') === '1';
    return fromUrl || fromStorage;
  });
  const [isChatExpanded, setIsChatExpanded] = useState(false);

  // Org config recommendations (Task 054 Phase 3)
  const [recommendations, setRecommendations] = useState<Array<{ key: string; priority: string; description: string }>>([]);

  // Sidebar state - managed by auto-collapse logic (collapses when chat OR detail panel is open)
  const shouldAutoCollapse = useSidebarAutoCollapse(isChatOpen, isDetailPanelOpen);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(shouldAutoCollapse);

  // Selected agent state (derived from URL)
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);

  // Sync sidebar collapsed state with auto-collapse logic
  useEffect(() => {
    setIsSidebarCollapsed(shouldAutoCollapse);
  }, [shouldAutoCollapse]);

  // Force-collapse left sidebar when chat is expanded to 50vw
  useEffect(() => {
    if (isChatExpanded) {
      setIsSidebarCollapsed(true);
    } else if (!shouldAutoCollapse) {
      setIsSidebarCollapsed(false);
    }
  }, [isChatExpanded, shouldAutoCollapse]);

  // Fetch recommendations from config-state endpoint (Task 054 Phase 3)
  useEffect(() => {
    if (!currentOrganization?.id) return;
    let cancelled = false;
    apiClient
      .get<{ recommendations?: Array<{ key: string; priority: string; description: string }> }>(
        '/control/v1/org/config-state'
      )
      .then(({ data }) => {
        if (!cancelled && data?.recommendations) {
          setRecommendations(data.recommendations);
        }
      })
      .catch(() => {}); // Silently ignore — not critical for layout
    return () => {
      cancelled = true;
    };
  }, [currentOrganization?.id]);

  // Guided onboarding flag — triggers auto-greeting from ASA.
  // Read synchronously from URL and sessionStorage during state init.
  // useState initializers run exactly once (StrictMode-safe).
  const [isGuidedSession, setIsGuidedSession] = useState(() => {
    const fromUrl = new URLSearchParams(window.location.search).get('guide') === '1';
    const fromStorage = sessionStorage.getItem('sb_guide_onboarding') === '1';
    if (fromStorage) {
      sessionStorage.removeItem('sb_guide_onboarding');
    }
    return fromUrl || fromStorage;
  });

  // Clean ?guide=1 from URL bar after state was read by initializers above.
  // Also acts as fallback: if initializers missed it (HMR edge case), set state here.
  useEffect(() => {
    const fromUrl = new URLSearchParams(location.search).get('guide') === '1';
    if (fromUrl) {
      // Ensure state is set (idempotent — no harm if initializers already did this)
      setIsChatOpen(true);
      setIsGuidedSession(true);
      // Remove ?guide=1 from URL bar so it doesn't re-trigger on re-renders
      const url = new URL(window.location.href);
      url.searchParams.delete('guide');
      window.history.replaceState({}, '', url.pathname + url.search);
    }
  }, [location.search]);

  // Reset expanded state when chat closes
  useEffect(() => {
    if (!isChatOpen) setIsChatExpanded(false);
  }, [isChatOpen]);

  // Auto-open sidebar when onboarding is in_progress (state-driven).
  // This handles the case where user returns after closing the sidebar
  // or navigates back from another page — the guided flow resumes.
  useEffect(() => {
    if (isOnboardingActive && !isChatOpen) {
      setIsChatOpen(true);
      setIsGuidedSession(true);
    }
  }, [isOnboardingActive]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close chat on mobile by default
  useEffect(() => {
    if (isMobile) {
      setIsChatOpen(false);
    }
  }, [isMobile]);

  // Detect selected agent from route
  useEffect(() => {
    const path = location.pathname;
    const match = path.match(/\/agents\/([^/]+)/);
    if (match) {
      const slug = match[1];
      const agent = agents.find((a) => a.slug === slug);
      if (agent) {
        setSelectedAgentId(agent.id);
      }
    } else {
      setSelectedAgentId(null);
    }
  }, [location.pathname]);

  const handleToggleChat = useCallback(() => {
    setIsChatOpen((prev) => !prev);
  }, []);

  const handleToggleSidebar = useCallback(() => {
    setIsSidebarCollapsed((prev) => !prev);
  }, []);

  const handleSelectAgent = useCallback(
    (agentId: string) => {
      const agent = agents.find((a) => a.id === agentId);
      if (agent) {
        navigate(`/agents/${agent.slug}/dashboard`);
      }
    },
    [navigate]
  );

  const handleMobileNavigate = useCallback(() => {
    // Close chat when navigating on mobile
    if (isMobile) {
      setIsChatOpen(false);
    }
  }, [isMobile]);

  return (
    <div
      className={`flex h-screen w-full bg-background-root text-text-main overflow-hidden font-sans transition-colors duration-500 ${
        isMobile ? 'flex-col' : ''
      }`}
    >
      {/* Ambient background blurs */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0 opacity-40">
        <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-blue-500/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-amber-500/10 rounded-full blur-[120px]" />
      </div>

      {/* Left Sidebar - Hidden on mobile */}
      {!isMobile && (
        <Sidebar
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={handleToggleSidebar}
          selectedAgentId={selectedAgentId}
          onSelectAgent={handleSelectAgent}
        />
      )}

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col relative z-10 h-full overflow-hidden">
        {/* Top Bar */}
        <TopBar isChatOpen={isChatOpen} onToggleChat={handleToggleChat} />

        {/* Main Content + Chat Panel */}
        <main className="flex-1 flex overflow-hidden relative">
          {/* Page Content */}
          <div className="flex-1 flex flex-col min-w-0 bg-background-panel relative overflow-hidden transition-all duration-700 ease-[cubic-bezier(0.4,0,0.2,1)] border-t border-black/5 dark:border-white/5">
            <div className="flex-1 overflow-y-auto custom-scrollbar">
              <Suspense fallback={
                <div className="flex items-center justify-center h-full">
                  <div className="w-6 h-6 border-2 border-current border-t-transparent rounded-full animate-spin opacity-30" />
                </div>
              }>
                <Outlet />
              </Suspense>
            </div>
          </div>

          {/* Chat Panel - Different behavior on mobile vs desktop */}
          <div
            className={`transition-all duration-700 ease-[cubic-bezier(0.4,0,0.2,1)] h-full overflow-hidden shrink-0 ${
              isMobile
                ? `fixed inset-0 z-50 bg-background-panel ${
                    isChatOpen ? 'translate-y-0' : 'translate-y-full'
                  }`
                : `border-l border-black/5 dark:border-white/5 bg-background-panel shadow-[-20px_0_80px_rgba(0,0,0,0.1)] ${
                    isChatOpen
                      ? isChatExpanded ? 'w-[50vw]' : 'w-[380px] xl:w-[460px]'
                      : 'w-0'
                  }`
            }`}
          >
            <div className={`${isMobile ? 'w-full' : isChatExpanded ? 'w-[50vw]' : 'w-[380px] xl:w-[460px]'} h-full`}>
              {/* Mobile close button */}
              {isMobile && isChatOpen && (
                <div className="absolute top-4 right-4 z-50">
                  <button
                    onClick={handleToggleChat}
                    className="p-2 bg-black/10 dark:bg-white/10 rounded-full hover:bg-black/20 dark:hover:bg-white/20 transition-colors"
                    aria-label="Close chat"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              )}
              {isChatOpen && (
                <CopilotErrorBoundary>
                  <CopilotKit
                    runtimeUrl={runtimeUrl}
                    agent="meta_assistant"
                    headers={{
                      ...(currentOrganization?.id ? { 'X-Tenant-ID': currentOrganization.id } : {}),
                    }}
                  >
                    <RightSidebarCopilot
                      isCollapsed={false}
                      autoGreet={isGuidedSession}
                      onAutoGreetSent={() => setIsGuidedSession(false)}
                      completedSteps={onboardingProgress?.completed_steps}
                      recommendations={recommendations}
                      isExpanded={isChatExpanded}
                      onToggleExpand={() => setIsChatExpanded((prev) => !prev)}
                      onboardingMode={onboardingProgress?.status === 'in_progress'}
                      currentStep={onboardingProgress?.current_step}
                    />
                  </CopilotKit>
                </CopilotErrorBoundary>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      {isMobile && <MobileBottomNav onNavigate={handleMobileNavigate} />}

      {/* Toast notifications */}
      <Toaster
        position="bottom-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: 'var(--background-panel, #fff)',
            color: 'var(--text-main, #1a1a1a)',
            border: '1px solid var(--glass-border, rgba(0,0,0,0.1))',
            borderRadius: '10px',
            fontSize: '13px',
          },
        }}
      />
    </div>
  );
};

export default GlobalLayout;
